import { useEffect, useMemo, useState } from "react";
import { api, ApiError, type AvaliacaoRealizada } from "../api";
import type { Row } from "../types";
import { IconClose, IconStar, IconDownload } from "../icons";

/**
 * TODAS AS AVALIAÇÕES DE DESEMPENHO JÁ REALIZADAS DE UM COLABORADOR.
 *
 * É o que o "olhinho" da lista abre. Até aqui a avaliação era gravada
 * (avaliacoes_desempenho, banco `sercamp`) e não havia por onde LER o que o
 * gestor respondeu: a tela mostrava só a nota final. Agora cada avaliação abre
 * questão a questão, com o texto do formulário FO 094 usado naquele ciclo.
 *
 * A lista vem do servidor já casada com o formulário (`modelo`), porque o
 * formulário do cargo pode ter mudado entre um ciclo e outro — o que vale é o
 * que estava valendo na hora da avaliação.
 */
export default function AvaliacoesColaboradorModal({
  colaborador,
  onClose,
}: {
  colaborador: Row;
  onClose: () => void;
}) {
  const [avaliacoes, setAvaliacoes] = useState<AvaliacaoRealizada[] | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  /** Qual avaliação está aberta (a mais recente já abre). */
  const [abertaId, setAbertaId] = useState<number | null>(null);

  const colaboradorId = Number(colaborador.id);

  useEffect(() => {
    let cancel = false;
    setAvaliacoes(null);
    setErro(null);
    api.avaliacaoLinks
      .doColaborador(colaboradorId)
      .then((d) => {
        if (cancel) return;
        setAvaliacoes(d.avaliacoes ?? []);
        setAbertaId(d.avaliacoes?.[0]?.id ?? null);
      })
      .catch((e) => !cancel && setErro(e instanceof ApiError ? e.message : "Erro ao carregar."));
    return () => {
      cancel = true;
    };
  }, [colaboradorId]);

  const media = useMemo(() => {
    const notas = (avaliacoes ?? []).map((a) => Number(a.nota)).filter((n) => Number.isFinite(n));
    if (!notas.length) return null;
    return notas.reduce((a, b) => a + b, 0) / notas.length;
  }, [avaliacoes]);

  return (
    <div className="modal-overlay" onMouseDown={onClose}>
      <div className="modal-card modal-ficha" onMouseDown={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h3>
            Avaliações de desempenho — {String(colaborador.nome ?? "colaborador")}
          </h3>
          <button type="button" className="icon-btn" onClick={onClose} title="Fechar">
            <IconClose size={18} />
          </button>
        </div>

        <div className="modal-body avaliacoes-historico">
          {erro ? (
            <div className="form-error">{erro}</div>
          ) : !avaliacoes ? (
            <div className="empty">Carregando…</div>
          ) : avaliacoes.length === 0 ? (
            <div className="empty">
              Nenhuma avaliação registrada para {String(colaborador.nome ?? "este colaborador")} ainda.
              <br />
              As avaliações aparecem aqui assim que o gestor envia o formulário do link, ou assim que o
              RH preenche pelo botão “Avaliar (FO 094)”.
            </div>
          ) : (
            <>
              <div className="ficha-cards">
                <div className="ficha-card">
                  <div className="ficha-card-valor">{avaliacoes.length}</div>
                  <div className="ficha-card-label">Avaliação(ões)</div>
                </div>
                <div className="ficha-card">
                  <div className="ficha-card-valor">{media != null ? media.toFixed(1) : "—"}</div>
                  <div className="ficha-card-label">Média das notas</div>
                </div>
                <div className="ficha-card">
                  <div className="ficha-card-valor">
                    {avaliacoes[0].nota != null ? Number(avaliacoes[0].nota).toFixed(1) : "—"}
                  </div>
                  <div className="ficha-card-label">Última nota</div>
                </div>
              </div>

              {avaliacoes.map((a) => (
                <AvaliacaoItem
                  key={a.id}
                  avaliacao={a}
                  aberta={abertaId === a.id}
                  onToggle={() => setAbertaId((atual) => (atual === a.id ? null : a.id))}
                />
              ))}
            </>
          )}
        </div>

        <div className="modal-foot">
          {(avaliacoes?.length ?? 0) > 0 && (
            <button
              type="button"
              className="btn-secondary"
              onClick={() => imprimir(colaborador, avaliacoes ?? [], setErro)}
            >
              <IconDownload size={16} /> Imprimir
            </button>
          )}
          <button type="button" className="btn-secondary" onClick={onClose}>
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
}

/** Escape de HTML para a folha de impressão. */
function escapar(s: unknown): string {
  return String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/**
 * Folha de impressão com o histórico inteiro — uma avaliação por bloco, com as
 * questões respondidas. Mesma abordagem do plano de ação: janela nova, para
 * imprimir só isto (e não a tela do sistema em volta).
 */
function imprimir(
  colaborador: Row,
  avaliacoes: AvaliacaoRealizada[],
  setErro: (m: string) => void,
) {
  const blocos = avaliacoes
    .map((a) => {
      const respostas = a.criterios ?? {};
      const questoes = a.modelo?.questoes ?? [];
      const linhas = questoes
        .filter((q) => respostas[q.chave] != null)
        .map(
          (q) =>
            `<tr><td><strong>${escapar(q.titulo)}</strong>${
              q.descricao ? `<br><span class="peq">${escapar(q.descricao)}</span>` : ""
            }</td><td class="nota">${escapar(respostas[q.chave])}</td></tr>`,
        )
        .join("");
      const textos = [
        a.pontos_fortes ? `<h3>Pontos fortes</h3><p class="txt">${escapar(a.pontos_fortes)}</p>` : "",
        a.pontos_melhoria ? `<h3>Pontos de melhoria</h3><p class="txt">${escapar(a.pontos_melhoria)}</p>` : "",
        a.comentario ? `<h3>Observações</h3><p class="txt">${escapar(a.comentario)}</p>` : "",
      ].join("");
      return `<section>
        <h2>${escapar(a.periodo || "Período não informado")} — nota ${
          a.nota != null ? escapar(Number(a.nota).toFixed(1)) : "—"
        }</h2>
        <p class="linha">${escapar(a.modelo_nome || a.modelo?.nome || "Formulário não identificado")} ·
          avaliado por ${escapar(a.avaliador || "—")}${a.origem ? ` · ${escapar(a.origem)}` : ""} ·
          ${escapar(dataBR(a.created_at))}</p>
        ${linhas ? `<table><thead><tr><th>Questão</th><th class="nota">Nota</th></tr></thead><tbody>${linhas}</tbody></table>` : `<p class="peq">Registrada só com a nota final.</p>`}
        ${textos}
      </section>`;
    })
    .join("");

  const html = `<!doctype html><html lang="pt-BR"><head><meta charset="utf-8">
    <title>Avaliações de desempenho — ${escapar(colaborador.nome)}</title>
    <style>
      *{box-sizing:border-box} body{font-family:system-ui,Segoe UI,sans-serif;color:#13241b;margin:28px}
      h1{font-size:19px;margin:0 0 4px} h2{font-size:14px;margin:0 0 4px;color:#0c7a3f}
      h3{font-size:12px;margin:10px 0 2px}
      .cab{border-bottom:2px solid #0c7a3f;padding-bottom:10px;margin-bottom:14px}
      .linha{font-size:12px;color:#444;margin:2px 0 8px}
      section{margin:0 0 22px;page-break-inside:avoid}
      table{width:100%;border-collapse:collapse;font-size:12px}
      th,td{border:1px solid #cbd8d0;padding:6px 8px;text-align:left;vertical-align:top}
      th{background:#eaf5ee;font-size:11px;text-transform:uppercase;letter-spacing:.04em}
      .nota{width:64px;text-align:center;font-weight:600}
      .peq{font-size:11px;color:#5e7268}
      .txt{font-size:12.5px;line-height:1.5;white-space:pre-wrap;margin:0}
      @media print{body{margin:12mm}}
    </style></head><body>
    <div class="cab">
      <h1>Avaliações de desempenho</h1>
      <p class="linha"><strong>${escapar(colaborador.nome)}</strong> ·
        ${escapar(colaborador.cargo ?? "—")} · ${escapar(colaborador.departamento ?? "—")} ·
        ${avaliacoes.length} avaliação(ões)</p>
    </div>
    ${blocos}
    </body></html>`;

  const janela = window.open("", "_blank");
  if (!janela) {
    setErro("O navegador bloqueou a janela de impressão. Libere os pop-ups deste site.");
    return;
  }
  janela.document.write(html);
  janela.document.close();
  janela.focus();
  janela.print();
}

/** Data legível (o servidor manda ISO). */
function dataBR(v: string | null): string {
  if (!v) return "—";
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString("pt-BR");
}

/** Verde/amarelo/vermelho pela nota, na mesma régua do consolidado. */
function tom(n: number | null): string {
  if (n == null) return "";
  return n >= 7 ? "ficha-card-ok" : n >= 5 ? "ficha-card-warn" : "ficha-card-danger";
}

/**
 * Uma avaliação da lista: cabeçalho sempre visível (período, nota, quem
 * avaliou) e, aberta, o formulário respondido questão a questão.
 *
 * Declarado FORA do componente de cima de propósito: componente declarado
 * dentro de outro remonta a cada render e faz a tela pular.
 *
 * Exportado porque a página pública do gestor (AvaliacaoPublica) mostra as
 * avaliações que ELE enviou com exatamente o mesmo desenho.
 */
export function AvaliacaoItem({
  avaliacao: a,
  aberta,
  onToggle,
  titulo,
}: {
  avaliacao: AvaliacaoRealizada;
  aberta: boolean;
  onToggle: () => void;
  /** Substitui o período no cabeçalho — na visão do gestor o que identifica
   *  cada bloco é o NOME do colaborador (o período é o mesmo para todos). */
  titulo?: string;
}) {
  const respostas = a.criterios ?? {};
  const questoes = a.modelo?.questoes ?? [];
  /** Questões respondidas, na ordem do formulário. */
  const respondidas = questoes.filter((q) => respostas[q.chave] != null);
  /**
   * Respostas que não casam com nenhuma questão do formulário atual: avaliação
   * antiga, ou formulário que mudou depois. Some-las seria esconder resposta
   * que a pessoa deu — então aparecem com a chave crua.
   */
  const soltas = Object.keys(respostas).filter((k) => !questoes.some((q) => q.chave === k));

  return (
    <div className="aval-item">
      <button type="button" className="aval-item-head" onClick={onToggle}>
        <span className={`aval-nota ${tom(a.nota != null ? Number(a.nota) : null)}`}>
          {a.nota != null ? Number(a.nota).toFixed(1) : "—"}
        </span>
        <span className="aval-item-info">
          <strong>{titulo || a.periodo || "Período não informado"}</strong>
          <small>
            {titulo && a.periodo ? `${a.periodo} · ` : ""}
            {a.modelo_nome || a.modelo?.nome || "Formulário não identificado"}
            {" · "}
            avaliado por {a.avaliador || "—"}
            {a.origem ? ` · ${a.origem}` : ""}
            {" · "}
            {dataBR(a.created_at)}
          </small>
        </span>
        <span className="aval-item-toggle">{aberta ? "Fechar" : "Ver respostas"}</span>
      </button>

      {aberta && (
        <div className="aval-item-corpo">
          {respondidas.length === 0 && soltas.length === 0 ? (
            <p className="empty">
              Esta avaliação foi registrada só com a nota final — não há respostas questão a questão.
            </p>
          ) : (
            <table className="aval-tabela">
              <thead>
                <tr>
                  <th>Questão</th>
                  <th className="aval-col-nota">Nota</th>
                </tr>
              </thead>
              <tbody>
                {respondidas.map((q) => (
                  <tr key={q.chave}>
                    <td>
                      <strong>{q.titulo}</strong>
                      {q.descricao && <small>{q.descricao}</small>}
                      <em className="aval-tipo">{q.tipo === "geral" ? "Geral" : "Específica da função"}</em>
                    </td>
                    <td className="aval-col-nota">
                      <span className={`aval-nota-mini ${tom(Number(respostas[q.chave]))}`}>
                        {respostas[q.chave]}
                      </span>
                    </td>
                  </tr>
                ))}
                {soltas.map((k) => (
                  <tr key={k}>
                    <td>
                      <strong>{k}</strong>
                      <small>Resposta de um formulário anterior (a questão não existe no atual).</small>
                    </td>
                    <td className="aval-col-nota">
                      <span className={`aval-nota-mini ${tom(Number(respostas[k]))}`}>{respostas[k]}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {(a.pontos_fortes || a.pontos_melhoria || a.comentario) && (
            <div className="aval-textos">
              {a.pontos_fortes && (
                <div>
                  <h5>
                    <IconStar size={14} /> Pontos fortes
                  </h5>
                  <p>{a.pontos_fortes}</p>
                </div>
              )}
              {a.pontos_melhoria && (
                <div>
                  <h5>Pontos de melhoria</h5>
                  <p>{a.pontos_melhoria}</p>
                </div>
              )}
              {a.comentario && (
                <div>
                  <h5>Observações</h5>
                  <p>{a.comentario}</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
